import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Check, AlertCircle, Loader } from 'lucide-react';
import NewsletterService from '../services/NewsletterService';

const NewsletterSubscription = ({ 
  title = "Stay Updated", 
  placeholder = "Enter your email",
  className = "",
  size = "default" // "default" | "compact" | "large"
}) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setStatus('error');
      setMessage('Please enter your email address');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      // Segmentation fields (source, signup date, interest) are set
      // server-side by the worker proxy - the client only sends the email.
      const result = await NewsletterService.subscribe(email);

      setStatus('success');
      setMessage(result.message);
      setEmail(''); // Clear form on success
      
      // Auto-reset after 5 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);

    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Something went wrong. Please try again.');
      
      // Auto-reset error after 3 seconds
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 3000);
    }
  };

  const getButtonContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <Loader size={16} className="animate-spin" />
            <span>Subscribing...</span>
          </>
        );
      case 'success':
        return (
          <>
            <Check size={16} />
            <span>Welcome!</span>
          </>
        );
      default:
        return (
          <>
            <Mail size={16} />
            <span>Subscribe</span>
          </>
        );
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <Check size={16} className="text-green-500" />;
      case 'error':
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return null;
    }
  };

  const sizeClasses = {
    compact: {
      input: 'text-sm py-2 px-3',
      button: 'text-sm py-2 px-3',
      container: 'gap-2'
    },
    default: {
      input: 'py-3 px-4',
      button: 'py-3 px-4',
      container: 'gap-2'
    },
    large: {
      input: 'text-lg py-4 px-5',
      button: 'text-lg py-4 px-5',
      container: 'gap-3'
    }
  };

  const currentSize = sizeClasses[size] || sizeClasses.default;

  return (
    <div className={`newsletter-subscription ${className}`}>
      {title && <h5 className="newsletter-title">{title}</h5>}
      
      <form onSubmit={handleSubmit} className="newsletter-form">
        <div className={`newsletter-input-container ${currentSize.container}`}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={placeholder}
            className={`newsletter-input ${currentSize.input} ${
              status === 'error' ? 'error' : status === 'success' ? 'success' : ''
            }`}
            disabled={status === 'loading' || status === 'success'}
            required
          />
          
          <motion.button
            type="submit"
            className={`newsletter-button ${currentSize.button} ${status}`}
            disabled={status === 'loading' || status === 'success'}
            whileHover={status === 'idle' ? { scale: 1.02 } : {}}
            whileTap={status === 'idle' ? { scale: 0.98 } : {}}
          >
            {getButtonContent()}
          </motion.button>
        </div>

        {message && (
          <motion.div
            className={`newsletter-message ${status}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {getStatusIcon()}
            <span>{message}</span>
          </motion.div>
        )}
      </form>

      {/* Privacy note */}
      <p className="newsletter-privacy">
        Get tips, updates and exclusive features. Unsubscribe anytime.
      </p>
    </div>
  );
};

export default NewsletterSubscription;
